/* global google */
import React, { useState, useEffect, useRef } from "react";
import "./index.css";

const MapPicker = ({
  onClose,
  onSelect,
  lang,
  disclosureId,
  setValue,
  enPhrase,
  arPhrase,
  navHeight,
  disclosureData,
}) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [outsideUAE, setOutsideUAE] = useState(false);

  useEffect(() => {
    const externalScript = document.createElement("script");
    externalScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCppCsio0715mEWqXlcXcJzWeyf3m5nir8&callback=initMap&libraries=places&v=weekly`;
    externalScript.async = true;
    externalScript.defer = true;
    document.head.appendChild(externalScript);

    return () => {
      document.head.removeChild(externalScript);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    setSelectedPosition(null);
    setSelectedAddress(null);
    setOutsideUAE(false);
  }, [disclosureId]);

  // const defaultLocation = { lat: 25.2792147241134, lng: 55.87850188281251 };
  const previous = disclosureData?.[disclosureId];
  const defaultLocation =
    previous?.latitude && previous?.longitude
      ? {
          lat: parseFloat(previous.latitude),
          lng: parseFloat(previous.longitude),
        }
      : { lat: 25.276987, lng: 55.296249 };

  useEffect(() => {
    window.initMap = () => {
      const map = new window.google.maps.Map(
        document.getElementById("mapRequest"),
        {
          center: defaultLocation,
          zoom: 12,
          mapTypeId: google.maps.MapTypeId.HYBRID,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          gestureHandling: "greedy",
        }
      );

      map.addListener("click", (mapsMouseEvent) => {
        marker.setPosition(mapsMouseEvent.latLng);
        map.setCenter(mapsMouseEvent.latLng);
      });

      map.addListener("center_changed", () => {
        marker.setPosition(map.center);
        //handleLocation(marker)
      });

      const input = document.getElementById("map-search-input");

      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ["establishment"],
        fields: ["geometry", "formatted_address", "name"],
      });

      autocomplete.setComponentRestrictions({
        country: ["ae"],
      });

      autocomplete.bindTo("bounds", map);
      const marker = new window.google.maps.Marker({
        map: map,
        draggable: true,
        position: defaultLocation,
        icon: {
          url: "/assets/img/add-request/AddLocation/Pin.svg",
          scaledSize: new google.maps.Size(72, 96),
        },
      });

      map.setCenter(defaultLocation);
      marker.setPosition(defaultLocation);
      map.setZoom(13);

      const centerControl = document.getElementById("reSetMap");

      centerControl?.addEventListener("click", () => {
        if (navigator?.permissions) {
          navigator?.permissions?.query({ name: "geolocation" }).then((res) => {
            const { state } = res;
          });
        } else {
          map.setCenter(defaultLocation);
          map.setZoom(10);
          marker.setPosition(defaultLocation);
        }
      });

      const zoomInControl = document.getElementById("zoomIn");
      zoomInControl?.addEventListener("click", () => {
        const newZoom = map.getZoom() + 1;
        map.setZoom(newZoom);
      });

      const zoomOutButton = document.getElementById("zoomOut");
      zoomOutButton?.addEventListener("click", () => {
        const newZoom = map.getZoom() - 1;
        map.setZoom(newZoom);
      });

      marker.addListener("dragend", () => {
        map.setCenter(marker.position);
        if (map.zoom >= 13) {
          return;
        } else {
          map.setZoom(13);
        }
      });

      map.addListener("dragstart", () => {});
      map.addListener("dragend", () => {});

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        marker.setPosition(place.geometry.location);
        map.setCenter(place.geometry.location);
        map.setZoom(13);
        marker.setVisible(true);
        if (!place.geometry || !place.geometry.location) {
          return;
        }
      });

      const geocoder = new google.maps.Geocoder();
      const updateAddress = (latlng) => {
        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === "OK" && results[0]) {
            const components = results[0].address_components;
            const country = components.find((c) => c.types.includes("country"));
            const countryCode = country ? country.short_name : "";
            setOutsideUAE(countryCode !== "AE");
            setSelectedAddress(results[0].formatted_address);
          } else {
            setSelectedAddress(`${latlng.lat}, ${latlng.lng}`);
            setOutsideUAE(true);
          }
        });
      };

      setSelectedPosition(defaultLocation);
      marker.setPosition(defaultLocation);
      map.setCenter(defaultLocation);
      updateAddress(defaultLocation);

      if (previous?.locationName) setSelectedAddress(previous.locationName);

      // google.maps.event.addListener(map, "center_changed", () => {
      //   const center = map.getCenter();
      //   const latlng = { lat: center.lat(), lng: center.lng() };
      //   marker.setPosition(center);
      //   setSelectedPosition(latlng);
      //   updateAddress(latlng);
      // });

      let debounceTimeout;
      google.maps.event.addListener(map, "center_changed", () => {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          const center = map.getCenter();
          const latlng = { lat: center.lat(), lng: center.lng() };
          marker.setPosition(center);
          setSelectedPosition(latlng);
          updateAddress(latlng);
        }, 500); // Call after 500ms of inactivity
      });
    };
  }, []);

  useEffect(() => {
    const repositionPACContainer = () => {
      const input = document.getElementById("map-search-input");
      const pacContainers = document.querySelectorAll(".pac-container");

      if (!input || pacContainers.length === 0) return;

      const rect = input.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;

      const top = rect.bottom + scrollY;
      const left = rect.left + scrollX;
      const width = rect.width;

      pacContainers.forEach((pac) => {
        Object.assign(pac.style, {
          position: "absolute",
          top: `${top}px`,
          left: `${left}px`,
          width: `${width}px`,
          zIndex: "99999",
          pointerEvents: "auto",
          direction: input.dir || document.documentElement.dir || "ltr",
        });
      });
    };

    // Initial delay for pac to mount
    // setTimeout(repositionPACContainer, 300);
    const inputEl = document.getElementById("map-search-input");
    inputEl?.addEventListener("focus", repositionPACContainer);

    const observer = new MutationObserver(() => {
      setTimeout(repositionPACContainer, 10);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", repositionPACContainer, true);
    window.addEventListener("resize", repositionPACContainer);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", repositionPACContainer, true);
      window.removeEventListener("resize", repositionPACContainer);
    };
  }, []);

  const handleDetectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latlng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          if (mapInstance.current && markerInstance.current) {
            mapInstance.current.setCenter(latlng);
            mapInstance.current.setZoom(15);
            markerInstance.current.setPosition(latlng);
            setSelectedPosition(latlng);

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: latlng }, (results, status) => {
              if (status === "OK" && results[0]) {
                const components = results[0].address_components;
                const country = components.find((c) =>
                  c.types.includes("country")
                );
                const countryCode = country ? country.short_name : "";
                setOutsideUAE(countryCode !== "AE");
                setSelectedAddress(results[0].formatted_address);
              }
            });
          }
        },
        () => alert("Location access denied or unavailable.")
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  return (
    <div className={`w-full  flex flex-col pt-0  xl:pt-[${navHeight}px]`}>
      {/* <div className="w-full  shadow-md  bg-gold-10 relative "> */}
      <div className="w-full min-w-[320px]  max-h-screen  shadow-md bg-gold-10 relative mx-auto ">
        <div className="w-full absolute top-[20px] md:top-[40px] xl:top-[20px] z-50">
          <div className="relative mx-auto 2xl:max-w-[1440px] xl:max-w-full lg:max-w-[960px] md:max-w-[100%] lg:px-[60px] sm:max-w-[100%] xl:py-0 px-[16px] md:px-[24px]">
            <div className="flex items-center justify-between ">
              <div className="relative flex items-center w-[85%] md:w-[95%]">
                <img
                  src="/assets/img/add-request/AddLocation/Search.svg"
                  alt="search"
                  className={`absolute ${
                    lang === "en" ? "left-3" : "right-3"
                  } top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded w-[24px] h-[24px]`}
                />
                <input
                  id="map-search-input"
                  ref={searchInputRef}
                  type="text"
                  placeholder={
                    lang === "en"
                      ? enPhrase["lblSeearchLocation"]
                      : arPhrase["lblSeearchLocation"]
                  }
                  className="w-full h-[56px] px-10 py-2 border font-Almarai text-[13px] leading-[20px] rounded-lg !border-gray-300 text-black focus:border-gold-100 focus:ring-gold-100 focus:bg-gold-10 "
                />
                <button
                  type="button"
                  onClick={() => {
                    if (searchInputRef.current) {
                      searchInputRef.current.value = "";
                    }
                  }}
                  className={`absolute ${
                    lang === "en" ? "right-3" : "left-3"
                  } top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded`}
                >
                  <img
                    src="/assets/img/add-request/AddLocation/clearSearch.svg"
                    alt="clear"
                    className="w-5 h-5"
                  />
                </button>
              </div>
              <img
                src="/assets/img/add-request/AddLocation/close.svg"
                alt=""
                className="flex cursor-pointer close-icon-fix"
                onClick={() => {
                  document.body.style.overflow = "unset";
                  mapInstance.current = null;
                  markerInstance.current = null;
                  // autocompleteRef.current = null;
                  onClose();
                }}
              />
            </div>
          </div>
        </div>

        {/* <div ref={mapRef} className="w-full h-[628px]  rounded" /> */}
        {/* <div
          ref={mapRef}
          dir="rtl"
          className="w-full h-[min(80vh,700px)] sm:h-[min(90vh,700px)] md:h-[calc(100vh-250px)]"
        /> */}

        {/* <div
          ref={mapRef}
          id="mapRequest"
          dir="rtl"
          className="w-full  2xl:h-[708px] xxl:h-[650px] xl:h-[460px] lg:h-[calc(100vh-350px)] md:h-[calc(100vh-140px)] h-[590px] "
        /> */}

        <div
          id="mapRequest"
          className="w-full  2xl:h-[708px] xxl:h-[650px] xl:h-[460px] lg:h-[calc(100vh-350px)] md:h-[calc(100vh-140px)] h-[590px]"
        ></div>

        <div className="flex items-center justify-between">
          {outsideUAE ? (
            <div className="w-full absolute bottom-[100px] z-0 flex justify-center pointer-events-none">
              <div
                className={`w-full 2xl:max-w-[1440px] xl:max-w-full lg:max-w-[960px] md:max-w-[100%]  lg:px-[60px]  px-[16px] md:px-[24px] ${
                  lang === "en"
                    ? "flex  justify-between items-end flex-row-reverse"
                    : "flex justify-between flex-row-reverse items-end"
                }`}
              >
                <div className="flex flex-col items-center justify-center gap-[8px]">
                  {/* Detect current location */}
                  <img
                    src="/assets/img/add-request/AddLocation/Send Button.svg"
                    alt="Detect Location"
                    onClick={handleDetectLocation}
                    className="cursor-pointer pointer-events-auto"
                  />

                  {/* Zoom In */}
                  <img
                    src="/assets/img/add-request/AddLocation/Plus Button.svg"
                    alt="Zoom In"
                    onClick={() => {
                      if (mapInstance.current) {
                        mapInstance.current.setZoom(
                          mapInstance.current.getZoom() + 1
                        );
                      }
                    }}
                    className="cursor-pointer pointer-events-auto"
                  />

                  {/* Zoom Out */}
                  <img
                    src="/assets/img/add-request/AddLocation/Minus Button.svg"
                    alt="Zoom Out"
                    onClick={() => {
                      if (mapInstance.current) {
                        mapInstance.current.setZoom(
                          mapInstance.current.getZoom() - 1
                        );
                      }
                    }}
                    className="cursor-pointer pointer-events-auto"
                  />
                </div>
                <div className="w-[287px] md:w-[566px] h-[72px] md:h-[64px]  rounded-[8px] px-[24px] py-[16px] gap-[10px] bg-[#E23636] flex items-center justify-center">
                  <img
                    src="/assets/img/add-request/AddLocation/errorRequest.svg"
                    alt="Error"
                    className="w-[40px] h-[32px]"
                  />
                  <span
                    className={`text-[#fff] font-Almarai  font-bold leading-[20px] ${
                      lang === "en" ? "md:leading-[22px]" : "md:leading-[32px]"
                    }  text-[12px] md:text-[18px]`}
                  >
                    {lang === "en"
                      ? enPhrase["lblOutsideLocation"]
                      : arPhrase["lblOutsideLocation"]}
                  </span>
                </div>
              </div>
            </div>
          ) : selectedPosition?.lat && selectedPosition?.lng ? (
            <div className="w-full absolute bottom-[100px] z-0 flex justify-center pointer-events-none">
              <div
                className={`w-full 2xl:max-w-[1440px] xl:max-w-full lg:max-w-[960px] md:max-w-[100%]  lg:px-[60px]  px-[16px] md:px-[24px] ${
                  lang === "en"
                    ? "flex justify-between flex-row-reverse"
                    : "flex justify-between flex-row-reverse"
                }`}
              >
                <div className="flex flex-col items-center justify-center gap-[8px]">
                  {/* Detect current location */}
                  <img
                    src="/assets/img/add-request/AddLocation/Send Button.svg"
                    alt="Detect Location"
                    onClick={handleDetectLocation}
                    className="cursor-pointer pointer-events-auto"
                  />

                  {/* Zoom In */}
                  <img
                    src="/assets/img/add-request/AddLocation/Plus Button.svg"
                    alt="Zoom In"
                    onClick={() => {
                      if (mapInstance.current) {
                        mapInstance.current.setZoom(
                          mapInstance.current.getZoom() + 1
                        );
                      }
                    }}
                    className="cursor-pointer pointer-events-auto"
                  />

                  {/* Zoom Out */}
                  <img
                    src="/assets/img/add-request/AddLocation/Minus Button.svg"
                    alt="Zoom Out"
                    onClick={() => {
                      if (mapInstance.current) {
                        mapInstance.current.setZoom(
                          mapInstance.current.getZoom() - 1
                        );
                      }
                    }}
                    className="cursor-pointer pointer-events-auto"
                  />
                </div>
                <div className="w-[287px] md:w-[566px] h-auto rounded-[4px] p-4 gap-[10px] bg-[#1A1A1A]/70 backdrop-blur-[10px]">
                  <p className="font-Almarai font-[600] text-[#fff] text-[16px] max-w-[200px] md:max-w-[450px]  whitespace-nowrap truncate ">
                    {selectedAddress}
                  </p>
                  <p className="font-Outfit font-[400] text-[#fff] text-[14px] leading-[24px] max-w-[200px] md:max-w-[350px]  whitespace-nowrap truncate">
                    {selectedPosition?.lat},{selectedPosition?.lng}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className=" bg-[#fff] flex items-center sticky bottom-0">
          <div className="w-full flex justify-center mx-auto 2xl:max-w-[1440px] xl:max-w-full lg:max-w-[960px] md:max-w-[100%] lg:px-[60px]  px-[16px] md:px-[24px] py-[14px]  ">
            <button
              onClick={() => {
                document.body.style.overflow = "unset";

                onSelect(selectedPosition, disclosureId, selectedAddress);
                // ✅ then update react-hook-form value
                setValue(
                  `location-${disclosureId}`,
                  `${selectedPosition.lat}, ${selectedPosition.lng}`
                );
              }}
              disabled={!selectedPosition || outsideUAE}
              className={` text-white px-6 py-2 rounded  w-full ${
                !selectedPosition || outsideUAE
                  ? "bg-gray-200 cursor-not-allowed"
                  : "bg-[#B68A35] cursor-pointer"
              }`}
            >
              <span
                className={`${
                  !selectedPosition || outsideUAE
                    ? "text-[#ACA7A0]"
                    : "text-[#fff]"
                } font-Almarai font-bold text-[14px] leading-[20px]`}
              >
                {" "}
                {lang === "en"
                  ? enPhrase["btnConfirmLocation"]
                  : arPhrase["btnConfirmLocation"]}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPicker;
